/**
 * Universal UTM attribution persistence.
 *
 * Checkout pages capture UTM / ad-click params from their URL and persist them
 * here; thank-you pages recover them after the post-payment redirect (which can
 * strip the URL query). Session-scoped (cleared when the tab closes), so it
 * never leaks attribution across browsing sessions.
 *
 * This is shared across ALL checkout flows (plans, webinar, offers, etc.) — do
 * not duplicate the storage logic per flow.
 */

const UTM_STORAGE_KEY = "aisensy_utm_params";

/**
 * Persist captured UTM params. Only non-empty captures are written, so a later
 * UTM-less navigation never wipes a valid earlier capture in the same session.
 * Never throws (sessionStorage can be unavailable in private mode).
 */
export function persistUtmParams(params) {
  try {
    if (params && typeof params === "object" && Object.keys(params).length > 0) {
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(params));
    }
  } catch (e) {
    // non-fatal — attribution persistence is best-effort
  }
}

/** Read previously-persisted UTM params. Returns {} if none / unavailable. */
export function getStoredUtmParams() {
  try {
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
}

/**
 * Resolve UTM params for a thank-you page. Prefers params already present in the
 * URL (passed in by the caller as `urlParams`); if the redirect stripped them,
 * recovers them from sessionStorage AND restores them into the URL via
 * history.replaceState so URL-based trackers (e.g. GA4) attribute the conversion
 * too. Returns the resolved params object (never throws).
 */
export function recoverUtmParams(urlParams) {
  if (urlParams && Object.keys(urlParams).length > 0) return urlParams;

  const stored = getStoredUtmParams();
  if (
    Object.keys(stored).length > 0 &&
    typeof window !== "undefined" &&
    window.history &&
    window.history.replaceState
  ) {
    try {
      const qs = new URLSearchParams(stored).toString();
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}?${qs}${window.location.hash || ""}`
      );
    } catch (e) {
      // non-fatal — URL restore is best-effort
    }
  }
  return stored;
}
